import api from '@/lib/api-client';
import { User } from '@/types/api';
import { UseQueryOptions, QueryKey, useQuery, useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { SnackBarContext } from 'pragmatic-ui';
import { useContext } from 'react';

const userKey = ['users'];

const getAdminUsers = async (): Promise<User[]> => {
  const response = await api.get('/v1/admin/users');
  return response.data;
};

export const useUsers = (
  options?: Omit<
    UseQueryOptions<User[], Error, User[], QueryKey>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    queryKey: userKey,
    queryFn: getAdminUsers,
    ...options,
  });

async function updateUser(payload: FormData): Promise<User> {
  const userId = payload.get('_id');
  if (!userId) {
    throw new Error('User id is required');
  }
  const response = await api.put(`/v1/admin/user/${userId}`, payload);
  return response.data;
}

export const useUpdateUser = (
  options?: Omit<UseMutationOptions<User, Error, FormData>, 'mutationFn'>,
) => {
  const queryClient = useQueryClient();
  const { showSnack } = useContext(SnackBarContext);

  return useMutation({
    mutationFn: updateUser,
    ...options,
    onSuccess: (uesr, ...rest) => {
      queryClient.invalidateQueries({ queryKey: userKey });
      options?.onSuccess?.(uesr, ...rest);
      showSnack('Successfully updated user', 'success');
    },
    onError: () => {
      showSnack('There was an unexpected error, please try again', 'error');
    },
  });
};

async function deleteUser(id: string): Promise<void> {
  const response = await api.delete(`/v1/admin/user/${id}`);
  return response.data;
}

export const useDeleteUser = (
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUser,
    ...options,
    onSuccess: (...rest) => {
      queryClient.invalidateQueries({ queryKey: userKey });
      options?.onSuccess?.(...rest);
    },
  });
};

export const sendInvite = async ({ email, name}: { email: string, name: string }) => {
  const response = await api.post('/v1/admin/send-invite', { email, name });
  return response.data;
}